import {
  FunctionComponent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'

import styled, { css } from 'styled-components'

import {
  desktopCSS,
  mobileAndTabletCSS,
  mobileCSS,
  tabletCSS,
} from '@measures/responsive'
import { triggerAureusDeboosting } from '@utils/aureus'
import { Link, Logo } from '@utils/cook/types'
import { WidgetKind } from '@utils/registry'
import AureusDeboostContext, {
  AureusDeboostContextType,
} from '@contexts/aureusDeboostContext'
import useViewportType from '@hooks/useViewport/useViewportType'
import { JSONWidgetType as AdRingierJSONWidgetType } from '@widgets/AdRingier'
import { TeaserStandardVerticalAPIProps } from '@widgets/TeaserStandardVertical'
import { CookWidget, JSONTypeForCookWidget } from '@widgets/types'
import BlickPlusSlides from '@components/Carousel/BlickPlusSlides'
import Slides from '@components/Carousel/Slides'
import { getSlidesWithAds } from '@components/Carousel/utils'
import SectionHeader from '@components/SectionHeader'
import Swiper, { ArrowsVisibilityType, SwiperClass } from '@components/Swiper'
import NavigationArrows from '@components/Swiper/NavigationArrows'

export type CarouselItem = TeaserStandardVerticalAPIProps & {
  articleId: string
  kind: WidgetKind
}

export type CarouselAd = AdRingierJSONWidgetType

export interface CarouselWidgetAPIProps {
  title: string
  accentColor: string
  items: CarouselItem[]
  link?: Link
  adSlideIndex?: number
  ad?: CarouselAd
  belongsTo: string
  logo?: Logo
  componentId?: string
  personalization?: {
    query: string
  }
}

const Separator = styled.div<{ position: 'top' | 'bottom' }>`
  ${({
    theme: {
      colors: { strokeWeak },
    },
    position,
  }) => css`
    display: block;

    width: 100%;
    height: 1px;
    background-color: ${strokeWeak};

    ${position === 'top' &&
    css`
      ${mobileAndTabletCSS(css`
        display: none;
      `)}
    `}
  `}
`

const CarouselWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing16 },
    },
  }) => css`
    width: 100%;
    height: 100%;

    display: grid;
    grid-row-gap: ${spacing16};
  `}
`

const StyledNavigationArrows = styled(NavigationArrows)<{ itemsCount: number }>`
  ${({ itemsCount }) => css`
    ${itemsCount <= 1 &&
    mobileCSS(css`
      display: none;
    `)};
    ${itemsCount <= 2 &&
    tabletCSS(css`
      display: none;
    `)};
    ${itemsCount <= 3 &&
    desktopCSS(css`
      display: none;
    `)}
  `}
`

const StyledSwiper = styled(Swiper)`
  ${({
    theme: {
      measures: { outerPadding },
    },
  }) => css`
    ${mobileCSS(css`
      margin-right: -${outerPadding.mobile}; // + stretch to right for mobile
      width: auto;

      > .swiper-items-container > *:last-child {
        margin-right: ${outerPadding.mobile};
        scroll-margin-right: ${outerPadding.mobile};
      }
    `)}
  `}
`

const Carousel: FunctionComponent<CarouselWidgetAPIProps> = ({
  title,
  accentColor,
  link,
  ad,
  adSlideIndex,
  items,
  logo,
  componentId,
  personalization,
}) => {
  const viewportType = useViewportType()
  const isDesktop = viewportType === 'desktop'

  const swiperRef = useRef<SwiperClass | null>(null)
  const [arrowsVisibility, setArrowsVisibility] =
    useState<ArrowsVisibilityType>()

  const itemsWithAds = useMemo(
    () =>
      adSlideIndex && ad ? getSlidesWithAds(items, adSlideIndex, ad) : items,
    [items, ad, adSlideIndex],
  )

  const onInit = useCallback(
    (swiper: any) => {
      if (swiperRef) {
        swiperRef.current = swiper
      }
    },
    [swiperRef],
  )

  const onArrowsVisibility = useCallback((arrow: ArrowsVisibilityType) => {
    setArrowsVisibility(arrow)
  }, [])

  const aureusDeboostContextValue = useMemo<AureusDeboostContextType>(
    () => ({
      deboostOnClick: ({ aureusOfferId, articleId }) => {
        if (!aureusOfferId || !articleId) {
          return
        }
        triggerAureusDeboosting({
          itemsToDeboost: [{ offerId: aureusOfferId, contentId: articleId }],
        })
      },
    }),
    [],
  )

  if (!title || componentId === 'carousel.promotionmitbox') {
    return null
  }

  return (
    <AureusDeboostContext.Provider value={aureusDeboostContextValue}>
      <Separator position="top" />
      <CarouselWrapper>
        <SectionHeader
          title={title}
          accentColor={accentColor}
          link={link}
          logo={logo}
          isSubheader={true}
        >
          <StyledNavigationArrows
            swiperRef={swiperRef}
            arrowsVisibility={arrowsVisibility}
            itemsCount={itemsWithAds?.length ?? 0}
          />
        </SectionHeader>
        <StyledSwiper
          initialSlide={0}
          spaceTopBottom={0}
          slidesPerGroup={isDesktop ? 3 : 1}
          spaceBetween={isDesktop ? 32 : 16}
          onInit={onInit}
          onArrowsVisibility={onArrowsVisibility}
          slides={
            !!personalization
              ? BlickPlusSlides({ fallbackItems: items })
              : Slides({ itemsWithAds })
          }
        />
      </CarouselWrapper>
      <Separator position="bottom" />
    </AureusDeboostContext.Provider>
  )
}

const widget = {
  kind: ['carousel'],
  component: Carousel,
} as const satisfies CookWidget

export type WidgetType = typeof widget

export type JSONWidgetType = JSONTypeForCookWidget<WidgetType>

export default widget
